<template>
  <Layout>
    <template #header-container>
      <div class="mc-list-header">
        <PageHeader :title="title" isSticky>
          <template #banner>
            <div
              v-if="['CANCELED', 'FAILED'].includes(routeOptimizationStatus)"
              class="mc-ro-banner"
            >
              <i class="mc-ro-banner__icon icon-fill_alert-triangle"></i>
              <div class="mc-ro-banner__text">
                <template v-if="routeOptimizationStatus === 'FAILED'">
                  {{ isRoSwatActive ? $t('routeOptimization.alert.optimizationFailedSwat') : $t('routeOptimization.alert.optimizationFailed') }}
                </template>
                <template v-else-if="routeOptimizationStatus === 'CANCELED'">
                  <div v-html="$t('routeOptimization.alert.optimizationHasBeenCanceled')"></div>
                </template>
              </div>
            </div>
          </template>

          <template #afterTitle>
            <div class="d-flex gap-2">
              <QuotaCounter class="me-2" />
              <McButton
                variant="secondary"
                :text="$t('global.back')"
                icon="icon-outline_arrow-left"
                @click="$router.go(-1)"
              />
            </div>
          </template>
        </PageHeader>
      </div>
    </template>

    <h5>{{ $t('routeOptimization.label.departureInfo') }}</h5>
    <b-card>
      <div class="row">
        <div class="col-12 col-md-2">
          <p class="__title">{{ $t('routeOptimization.fields.originAddress') }}</p>
        </div>

        <div class="col-12 col-md-10">
          <p>{{ showResponse.start_address?.name }}</p>
          <hr class="m-0" />
        </div>
      </div>
    </b-card>

    <h5>{{ $t('routeOptimization.label.orderList') }}</h5>
    <McTable
      withCard
      :apiClient="apiClientDelivery"
      :fields="deliveryFields"
      :hasCheckbox="false"
      v-bind="deliveryTableProps"
    >
      <template #cell(created_on)="{ value }">
        <template>
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">{{ formatIndexDate(value).time }}</div>
        </template>
      </template>

      <template #cell(status.name)="{ value, item }">
        <template>
          <span
            v-if="item.status?.color"
            class="__status-dots"
            :style="{ backgroundColor: item.status.color }"
          ></span>
          <span class="mc-text-status">{{ setStatusTMS('delivery.status', value) }}</span>
        </template>
      </template>
    </McTable>

    <h5>{{ $t('routeOptimization.label.assignmentParameter') }}</h5>
    <div>
      <b-card>
        <h4 class="card-title">{{ $t('routeOptimization.label.deliveryTime') }}</h4>
        <div class="row">
          <div class="col-12 col-md-2">
            <div class="d-flex align-items-center gap-2">
              <p class="__title mb-0">{{ $t('routeOptimization.fields.departureTime') }}</p>
              <McInfoTooltip
                id="departure_time"
                :tooltip="$t('routeOptimization.tooltip.operationalHoursInfo')"
              />
            </div>
          </div>

          <div class="col-12 col-md-4">
            <p>{{ formatDate(showResponse.expected_departure_on) }}</p>
            <hr class="m-0" />
          </div>

          <div class="col-12 col-md-2">
            <p class="__title">{{ $t('routeOptimization.fields.assignmentEndTime') }}</p>
          </div>

          <div class="col-12 col-md-4">
            <p>{{ formatDate(showResponse.expected_arrival_on) }}</p>
            <hr class="m-0" />
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12 col-md-2">
            <div
              v-if="!showResponse.options?.is_service_time_per_activity"
              class="d-flex align-items-center gap-2"
            >
              <p class="__title mb-0">
                {{ $t('routeOptimization.fields.activityDurationPerAddress') }}
              </p>
              <McInfoTooltip
                id="service_time"
                :tooltip="$t('routeOptimization.tooltip.averagePickupDeliveryDuration')"
              />
            </div>
            <div v-else class="d-flex align-items-center gap-2">
              <p class="__title mb-0">{{ $t('routeOptimization.fields.activityDurationPerDO') }}</p>
              <McInfoTooltip
                id="service_time"
                :tooltip="$t('routeOptimization.tooltip.averagePickupDeliveryDuration')"
              />
            </div>
          </div>

          <div class="col-12 col-md-4">
            <p>{{ serviceTime }}</p>
            <hr class="m-0" />
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12 col-md-2">
            <div class="d-flex align-items-center gap-2">
              <p class="__title mb-0">{{ $t('routeOptimization.fields.breakTime') }}</p>
              <McInfoTooltip
                id="break_time"
                :tooltip="$t('routeOptimization.tooltip.dailyRouteBreakTime')"
              />
            </div>
          </div>

          <div class="col-12 col-md-4">
            <p>{{ breakTime }}</p>
            <hr class="m-0" />
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12 col-md-2">
            <div class="d-flex align-items-center gap-2">
              <p class="__title mb-0">{{ $t('routeOptimization.fields.traffic') }}</p>
              <McInfoTooltip
                id="traffic_level"
                :tooltip="$t('routeOptimization.tooltip.traffic')"
              />
            </div>
          </div>

          <div class="col-12 col-md-4">
            <p>{{ trafficLevel }}</p>
            <hr class="m-0" />
          </div>
        </div>

        <hr class="mt-4" />

        <h4 class="card-title mt-2">{{ $t('routeOptimization.label.routeOption') }}</h4>

        <div class="d-flex gap-3">
          <ChecklistInput
            v-if="!isRoSwatActive"
            id="minimum_vehicle"
            :label="$t('routeOptimization.fields.minimizeVehicles')"
            :tooltip="$t('routeOptimization.tooltip.minimizeVehicles')"
            :modelValue="showResponse.options?.minimum_vehicle"
            :disabled="true"
          />
          <ChecklistInput
            id="avoid_tolls"
            :label="$t('routeOptimization.fields.avoidToll')"
            :tooltip="$t('routeOptimization.tooltip.avoidToll')"
            :modelValue="showResponse.options?.avoid_tolls"
            :disabled="true"
          />
          <ChecklistInput
            v-if="!isRoSwatActive"
            id="round_trip"
            :label="$t('routeOptimization.fields.roundTrip')"
            :tooltip="$t('routeOptimization.tooltip.roundTrip')"
            :modelValue="showResponse.options?.round_trip"
            :disabled="true"
          />
          <ChecklistInput
            v-if="false"
            id="pickup_location_optimization"
            :label="$t('routeOptimization.fields.doPickupOptimization')"
            :tooltip="$t('routeOptimization.tooltip.doPickupOptimization')"
            :modelValue="showResponse.options?.pickup_location_optimization"
            :disabled="true"
          />
          <ChecklistInput
            v-if="hasAccessAll(['route-optimization-manually-assign-vehicle:read'])"
            id="manually_assign_vehicle"
            :label="$t('routeOptimization.fields.manuallyAssignVehicle')"
            :tooltip="$t('routeOptimization.tooltip.manuallyAssignVehicle')"
            :modelValue="showResponse.options?.manually_assign_vehicle"
            :disabled="true"
          />
        </div>

        <hr v-if="!isRoSwatActive" />

        <h4 v-if="!isRoSwatActive" class="card-title mt-2">{{ $t('routeOptimization.label.considerCapacity') }}</h4>

        <div v-if="!isRoSwatActive" class="d-flex gap-3">
          <ChecklistInput
            id="weight_capacity"
            :label="$t('routeOptimization.fields.basedOnWeight')"
            :tooltip="$t('routeOptimization.tooltip.basedOnWeight')"
            :modelValue="showResponse.options?.weight_capacity"
            :disabled="true"
          />
          <ChecklistInput
            id="volume_capacity"
            :label="$t('routeOptimization.fields.basedOnVolume')"
            :tooltip="$t('routeOptimization.tooltip.basedOnVolume')"
            :modelValue="showResponse.options?.volume_capacity"
            :disabled="true"
          />
          <ChecklistInput
            v-if="false"
            id="by_box_type"
            :label="$t('routeOptimization.fields.basedOnBoxType')"
            :tooltip="$t('routeOptimization.tooltip.basedOnBoxType')"
            :modelValue="showResponse.options?.box_type_capacity"
            :disabled="true"
          />
        </div>
      </b-card>
    </div>

    <h5>{{ $t('routeOptimization.label.selectVehicle') }}</h5>
    <McTable
      withCard
      :apiClient="apiClientVehicle"
      :fields="vehicleFields"
      :hasCheckbox="false"
      v-bind="deliveryTableProps"
    >
      <template v-slot:[`cell(license_plate)`]="{ item }">
        {{ item?.license_plate ?? '-' }}
        <div class="text-muted" v-if="item?.hull_no">
          {{ item?.hull_no }}
        </div>
      </template>
    </McTable>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McTable from '@/components/McTable'
import McInfoTooltip from '@/components/McInfoTooltip'
import ChecklistInput from './contents/SetParameters/components/ChecklistInput'
import * as RO from '@/services/rest-api/lib/route-optimization'
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'

import getDOListFields from './lib/fields/setting-delivery-list-fields.js'
import getVehicleFields from './lib/fields/setting-vehicle-list-fields.js'
import getTrafficLevels from './lib/constants/traffic-levels'
import QuotaCounter from './components/QuotaCounter'

export default {
  page: {
    title: 'Route Optimization',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McTable,
    ChecklistInput,
    McInfoTooltip,
    QuotaCounter,
  },
  mixins: [Helper],
  data() {
    return {
      isLoading: true,

      showResponse: {},
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    title() {
      return `${this.$t('routeOptimization.configuration')} #${this.showResponse.number || ''}`
    },
    id() {
      return this.$route.params.id
    },
    deliveryFields() {
      return getDOListFields(this)
    },
    vehicleFields() {
      return getVehicleFields(this)
    },
    apiClientDelivery() {
      return {
        get: RO.getSettingDelivery(this.id),
      }
    },
    apiClientVehicle() {
      return {
        get: RO.getSettingVehicle(this.id),
      }
    },
    routeOptimizationStatus() {
      return this.showResponse?.status?.raw_type || null
    },
    breakTime() {
      const startTime = this.showResponse?.options?.fleet_shift_time?.start_break_time
      const endTime = this.showResponse?.options?.fleet_shift_time?.end_break_time

      if (startTime && endTime) {
        return `${startTime} - ${endTime}`
      }
      return '-'
    },
    trafficLevels() {
      return getTrafficLevels(this)
    },
    trafficLevel() {
      const trafficLevel = this.showResponse?.options?.traffic_level
      const found = this.trafficLevels.find((level) => level.key === trafficLevel)
      return found ? found.label : '-'
    },
    serviceTime() {
      const duration = this.showResponse?.options?.service_time
      if (!duration) return `0 ${this.$t('global.time.minutes')}`

      const timeString = duration.replace('PT', '')
      const hoursMatch = timeString.match(/(\d+)H/)
      const minutesMatch = timeString.match(/(\d+)M/)

      const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0
      const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0

      const parts = []
      if (hours > 0) parts.push(`${hours} ${this.$t('global.time.hours')}`)
      if (minutes > 0) parts.push(`${minutes} ${this.$t('global.time.minutes')}`)

      return parts.length > 0 ? parts.join(' ') : `0 ${this.$t('global.time.minutes')}`
    },
    deliveryTableProps() {
      return {
        limitOptions: [5, 10, 25, 50, 100],
        initialLimit: 5,
        searchable: true,
      }
    },
    isRoSwatActive() {
      const isSamator = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false
      
      return isSamator && isActive
    },
  },
  mounted() {
    this.showData()
  },
  methods: {
    formatIndexDate,

    formatDate(date) {
      return formatIndexDate(date, 'DD MMM YYYY HH:mm').date
    },

    async showData() {
      const res = await RO.showSetting(this.id)

      if (res.errors) return

      this.showResponse = res.data
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.mc-ro-banner) {
  width: 100%;
  height: 72px;
  background-color: #fff3e6;
  font-size: 13px;
  padding: 0px 20px;
  margin-top: -24px;

  display: flex;
  align-items: center;
  gap: 40px;

  .mc-ro-banner__text {
    height: fit-content;
  }

  .mc-ro-banner__icon:before {
    color: #ff8a00;
    font-size: 20px;
  }
}

.mc-ro-banner + .mc-list-header {
  margin-top: 60px;
}

.__status-dots {
  display: inline-block;
  margin: auto;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
}

.__title {
  color: $mep-color-text-300;
}
</style>
